import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Tabs, Tab,
  Tooltip, LinearProgress, Alert, Autocomplete, IconButton,
} from '@mui/material';
import { CheckCircleOutline, ReceiptLong, EditOutlined } from '@mui/icons-material';
import {
  billsService, statusVar, type Bill, type BillsResponse, type BillStatus,
  type BillPayee, type BillPaymentMethod, type PayeeSuggestion,
  type BillForecast, type ForecastResponse,
} from '../services/bills-service';
import { accountsService } from '../services/accounts-service';
import { aiService, type AIInsightsResponse } from '../services/ai-service';
import type { Account } from '../types/api';
import { seriesColors } from '../styles/glass';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';
import { AccountSelect } from '../components/AccountSelect';
import { PayeeDialog, FUNDING_TYPES, type PayeeDraft } from '../components/bills/PayeeDialog';
import { QuickPayFeeDialog, type QuickPayTarget } from '../components/bills/QuickPayFeeDialog';
import { BillTransactionsDialog, type BillDrillTarget } from '../components/bills/BillTransactionsDialog';
import { needsAction } from '../utils/bill-action';
import { scheduleBadge, type ScheduleTone } from '../utils/bill-schedule';

/** Colour token per schedule badge tone. */
const SCHEDULE_TONE: Record<ScheduleTone, string> = {
  info: 'var(--info-text)', ok: 'var(--inflow-text)',
  warn: 'var(--warn-text)', danger: 'var(--danger-text)',
};

/** The scheduled-payment badge for a bill row; the note is on hover. */
const ScheduleChip: React.FC<{ bill: Bill }> = ({ bill }) => {
  const badge = scheduleBadge(bill);
  if (!badge) return null;
  const color = SCHEDULE_TONE[badge.tone];
  return (
    <Tooltip title={badge.detail}>
      <Chip size="small" label={badge.label} variant="outlined" data-testid="schedule-badge"
        sx={{ height: 18, color, borderColor: color }} />
    </Tooltip>
  );
};

// Bill amounts roll up across accounts, so they report in the base currency.
const fmt = (n: number | string | null | undefined) => baseMoney(n, 2);
const fmt0 = (n: number | string | null | undefined) => baseMoney(n, 0);

type TabKey = 'action' | 'paid' | 'all';

const PAYMENT_METHODS: BillPaymentMethod[] = [
  'Manual', 'AutoPay', 'Chexy', 'PreAuthorized', 'Unknown',
];

/** First of the month, as the YYYY-MM-DD the API expects. */
const monthStart = (offset = 0): string => {
  const d = new Date();
  return new Date(Date.UTC(d.getFullYear(), d.getMonth() + offset, 1))
    .toISOString().slice(0, 10);
};

/**
 * Bills — what is owed, what is due, and what has been paid.
 *
 * "Record payment" writes a ledger entry describing a payment the user already
 * made at their bank. The app never moves money, and the copy says so.
 */
export const BillsPage: React.FC = () => {
  // Subscribes the page so fmt() relabels when the base currency changes.
  useBaseCurrency();
  const [data, setData] = useState<BillsResponse | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('action');

  const [paying, setPaying] = useState<Bill | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payFrom, setPayFrom] = useState('');
  const [payStatement, setPayStatement] = useState('');
  const [payMinimum, setPayMinimum] = useState('');
  const [payMethod, setPayMethod] = useState<BillPaymentMethod | ''>('');
  const [payFee, setPayFee] = useState('');
  const [posting, setPosting] = useState<Bill | null>(null);
  const [postDate, setPostDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [postAmount, setPostAmount] = useState('');
  const [suggestions, setSuggestions] = useState<PayeeSuggestion[]>([]);
  const [payees, setPayees] = useState<BillPayee[]>([]);
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billEditing, setBillEditing] = useState<Bill | null>(null);
  const [billPayee, setBillPayee] = useState('');
  const [billPeriod, setBillPeriod] = useState(() => monthStart(1));
  const [billDue, setBillDue] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [billMinimum, setBillMinimum] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [payeeEditing, setPayeeEditing] = useState<BillPayee | null>(null);
  const [payeeDraft, setPayeeDraft] = useState<PayeeDraft | undefined>(undefined);
  const [feePay, setFeePay] = useState<QuickPayTarget | null>(null);
  // The bill whose transactions are open, from a click on its amount.
  const [drill, setDrill] = useState<BillDrillTarget | null>(null);
  const [ai, setAi] = useState<AIInsightsResponse | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => {
    billsService.list()
      .then(setData)
      .catch((e) => setError(e?.message ?? 'Failed to load bills'));
  };

  useEffect(() => {
    load();
    loadPayees();
    loadForecast();
    loadSuggestions();
    // Reads the cache only — this never calls the model.
    aiService.getInsights().then(setAi).catch(() => setAi(null));
    // EVERY account, paged. This used to be one getAccounts({ per_page: 200 })
    // call; the API caps a page at 100 and answered the oversize request with
    // its default of 20, so "Paid from" offered the 20 newest accounts and the
    // member's own older chequing account could not be picked at all.
    accountsService.getAllAccounts()
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, []);

  const bills = data?.bills ?? [];

  const filtered = useMemo(() => {
    switch (tab) {
      case 'action':
        // What the user actually has to do something about -- including a
        // bill due in the next few days, which the server still calls Upcoming.
        return bills.filter((b) => needsAction(b));
      case 'paid':
        return bills.filter((b) => b.effective_status === 'Paid');
      default:
        return bills.filter((b) => b.effective_status !== 'NoBalance');
    }
  }, [bills, tab]);

  const openPay = (b: Bill) => {
    setPaying(b);
    setPayAmount(b.amount_outstanding !== '0' ? b.amount_outstanding : (b.amount_due ?? ''));
    setPayDate(new Date().toISOString().slice(0, 10));
    // The account this bill was paid from, else the payee's funding account.
    // Never "the first account in the list": that defaulted every payment to
    // whichever card happened to sort first, and an unnoticed default is a
    // wrong ledger entry. Empty lets the server apply the payee's default.
    setPayFrom(b.paid_from_account_id
      ?? payees.find((p) => p.id === b.payee_id)?.funding_account_id
      ?? '');
    // Default the statement to what is on record, so "pay in full" is one click.
    setPayStatement(b.statement_balance ?? b.amount_due ?? '');
    setPayMinimum(b.minimum_due ?? '');
    // Defaults to how this payee is normally settled; overridable per payment.
    setPayMethod(b.payment_method ?? '');
    setPayFee(b.payment_fee ?? '');
    setNotice(null);
  };

  const openPosted = (b: Bill) => {
    setPosting(b);
    setPostDate(b.posted_on?.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
    setPostAmount(b.posted_amount ?? b.amount_paid ?? '');
    setNotice(null);
  };

  const submitPosted = async () => {
    if (!posting) return;
    setBusy(true);
    try {
      await billsService.confirmPosted(posting.id, {
        posted_on: postDate,
        posted_amount: postAmount || undefined,
      });
      setPosting(null);
      setNotice(`Confirmed ${fmt(postAmount)} posted for ${posting.payee_name}.`);
      load();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to confirm posting');
    } finally {
      setBusy(false);
    }
  };

  const loadForecast = () => {
    billsService.forecast(monthStart(1)).then(setForecast).catch(() => setForecast(null));
  };

  const loadPayees = () => {
    billsService.listPayees().then(setPayees).catch(() => setPayees([]));
  };

  const loadSuggestions = () => {
    billsService.suggestions().then(setSuggestions).catch(() => setSuggestions([]));
  };

  /** Pre-fill the add form from a detected recurring payment. */
  const acceptSuggestion = (sg: PayeeSuggestion) => {
    setPayeeEditing(null);
    setPayeeDraft({
      name: sg.name, category: sg.category || 'Other',
      typical_due_day: sg.typical_day || 1,
      funding_account_id: sg.funding_account_id ?? null,
    });
    setAddOpen(true);
  };

  /** Enter (or correct) one month's bill for an existing payee. */
  const openBill = (b?: Bill) => {
    setBillEditing(b ?? null);
    setBillPayee(b?.payee_id ?? payees[0]?.id ?? '');
    setBillPeriod(b?.period.slice(0, 10) ?? monthStart(1));
    setBillDue(b?.due_date?.slice(0, 10) ?? '');
    setBillAmount(b?.amount_due ?? '');
    setBillMinimum(b?.minimum_due ?? '');
    setNotice(null);
    setBillOpen(true);
  };

  // Reason: due day follows the payee unless the statement says otherwise, so
  // picking a payee fills the date the user would have typed anyway.
  const onBillPayeeChange = (id: string) => {
    setBillPayee(id);
    const p = payees.find((x) => x.id === id);
    if (p?.typical_due_day && !billEditing) {
      const [y, m] = billPeriod.split('-').map(Number);
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      const day = Math.min(p.typical_due_day, last);
      setBillDue(`${billPeriod.slice(0, 8)}${String(day).padStart(2, '0')}`);
    }
  };

  const submitBill = async () => {
    setBusy(true);
    try {
      await billsService.saveBill({
        id: billEditing?.id,
        payee_id: billPayee,
        period: billPeriod,
        due_date: billDue || undefined,
        amount_due: billAmount || undefined,
        minimum_due: billMinimum || undefined,
        // An amount that is owed and not yet paid is Due; without one it is
        // just a placeholder for the month.
        status: billEditing?.status ?? (billAmount ? 'Due' : 'Upcoming'),
      });
      setBillOpen(false);
      const name = payees.find((p) => p.id === billPayee)?.name ?? 'Bill';
      setNotice(`Saved ${name} for ${billPeriod.slice(0, 7)}.`);
      load();
      loadForecast();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to save bill');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Settle a bill by repeating last month's account and rail.
   *
   * The notice names the basis the server used, so an amount copied from the
   * previous payment is never quietly presented as a billed one.
   */
  const quickPay = async (
    billId: string, payeeName: string, input: { amount?: string; payment_fee?: string } = {},
  ) => {
    setBusy(true);
    try {
      const r = await billsService.quickPay(billId, input);
      setFeePay(null);
      const from = r.amount_basis === 'previous payment'
        ? ' — copied from the previous payment, not a statement'
        : '';
      setNotice(`Recorded ${fmt(r.bill.amount_paid)} against ${payeeName}${from}. No money was moved.`);
      load();
      loadForecast();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to quick pay');
    } finally {
      setBusy(false);
    }
  };

  /** Open next month for every active payee, ready to fill in amounts. */
  const rollForward = async () => {
    setBusy(true);
    try {
      const r = await billsService.rollForward(monthStart(1));
      setNotice(r.created > 0
        ? `Opened ${r.period.slice(0, 7)} — ${r.created} bill${r.created === 1 ? '' : 's'} ready for amounts.`
        : `${r.period.slice(0, 7)} is already open. Nothing was changed.`);
      load();
      loadForecast();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to open next month');
    } finally {
      setBusy(false);
    }
  };

  const openAdd = () => {
    setPayeeEditing(null);
    setPayeeDraft(undefined);
    setAddOpen(true);
  };

  const openEditPayee = (payeeId: string) => {
    const p = payees.find((x) => x.id === payeeId);
    if (!p) return;
    setPayeeEditing(p);
    setPayeeDraft(undefined);
    setAddOpen(true);
  };

  const submitPayee = async (p: Partial<BillPayee> & { name: string }) => {
    setBusy(true);
    try {
      await billsService.savePayee(p);
      setAddOpen(false);
      setNotice(payeeEditing
        ? `Saved ${p.name}.`
        : `Added ${p.name}. Bills for it can now be tracked.`);
      load();
      loadPayees();
      loadSuggestions();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to add payee');
    } finally {
      setBusy(false);
    }
  };

  /** Explicit user action: the only path that can spend an API call. */
  const refreshInsights = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      setAi(await aiService.refresh());
    } catch (e: any) {
      setAiError(e?.message ?? 'Could not generate insights');
    } finally {
      setAiBusy(false);
    }
  };

  const runReconcile = async () => {
    setBusy(true);
    try {
      const r = await billsService.reconcile();
      setNotice(r.matched > 0
        ? `Matched ${r.matched} payment${r.matched === 1 ? '' : 's'} to bank transactions.`
        : 'No new matches. Anything still flagged has no matching transaction.');
      load();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to reconcile');
    } finally {
      setBusy(false);
    }
  };

  const submitPayment = async () => {
    if (!paying) return;
    setBusy(true);
    try {
      await billsService.recordPayment(paying.id, {
        amount: payAmount,
        paid_on: payDate,
        from_account_id: payFrom || undefined,
        statement_balance: payStatement || undefined,
        minimum_due: payMinimum || undefined,
        payment_method: payMethod || undefined,
        payment_fee: payFee || undefined,
      });
      setPaying(null);
      setNotice(`Recorded ${fmt(payAmount)} against ${paying.payee_name}. No money was moved.`);
      load();
      loadForecast();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to record payment');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;
  if (!data) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading bills…</Typography>
      </Box>
    );
  }

  const s = data.summary;
  const maxCat = Math.max(1, ...data.categories.map((c) => Number(c.total_paid)));
  const palette = seriesColors('dark');

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Obligations</Typography>
          <Typography variant="h3">Bills</Typography>
          <Typography variant="body2" color="text.secondary">
            {data.from.slice(0, 7)} — {data.to.slice(0, 7)} · what&apos;s owed, due, and paid
          </Typography>
        </Box>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ textAlign: 'right' }}>
          {!ai?.insights && (
            <Tooltip title="Analyse your bills. Uses one AI call.">
              <span>
                <Button size="small" variant="outlined" onClick={refreshInsights} disabled={aiBusy}>
                  {aiBusy ? 'Analysing…' : 'Insights'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button size="small" variant="contained" onClick={() => openBill()}
            disabled={payees.length === 0}>
            Add bill
          </Button>
          <Tooltip title="Create next month's bills for every active payee, ready to fill in">
            <span>
              <Button size="small" variant="outlined" onClick={rollForward} disabled={busy}>
                Open {new Date(monthStart(1)).toLocaleString(undefined, { month: 'short', timeZone: 'UTC' })}
              </Button>
            </span>
          </Tooltip>
          <Button size="small" variant="outlined" onClick={openAdd}>Add payee</Button>
          <Tooltip title="Match bills you marked paid against real bank transactions">
            <span>
              <Button size="small" variant="outlined" onClick={runReconcile} disabled={busy}>
                {busy ? 'Matching…' : 'Reconcile'}
              </Button>
            </span>
          </Tooltip>
          <Metric label="Outstanding" value={fmt(s.outstanding)}
            varName={Number(s.outstanding) > 0 ? '--warn-text' : '--inflow-text'} />
          <Metric label="Paid" value={fmt0(s.total_paid)} varName="--inflow-text" />
          <Metric label="Billed" value={fmt0(s.total_due)} varName="--text" />
        </Stack>
      </Stack>

      {(ai?.insights || aiError) && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Insights</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Observations about your bills. Descriptive only — never advice.
                  {ai?.generated_at ? ` Generated ${new Date(ai.generated_at).toLocaleString()}.` : ''}
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={refreshInsights} disabled={aiBusy}>
                {aiBusy ? 'Analysing…' : 'Refresh'}
              </Button>
            </Stack>

            {aiError && <Alert severity="warning" sx={{ mb: 1 }}>{aiError}</Alert>}

            {ai?.reason && (
              <Typography variant="caption" sx={{ color: 'var(--text-faint)', display: 'block', mb: 1 }}>
                {ai.reason} — no new analysis was needed.
              </Typography>
            )}

            {ai?.insights?.summary && (
              <Typography variant="body2" sx={{ mb: 1.5 }}>{ai.insights.summary}</Typography>
            )}

            {ai?.insights?.insights?.map((it, i) => (
              <Box key={i} sx={{
                display: 'flex', gap: 1.5, py: 1,
                borderTop: '1px solid var(--border-soft)',
              }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%', mt: '7px', flexShrink: 0,
                  background: it.severity === 'action' ? 'var(--danger-text)'
                    : it.severity === 'watch' ? 'var(--warn-text)' : 'var(--inflow-text)',
                }} />
                <Box>
                  <Typography variant="body2" fontWeight={650}>{it.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{it.detail}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {forecast && forecast.forecast.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
              sx={{ mb: 1 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Coming up · {new Date(forecast.period).toLocaleString(undefined,
                    { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Priced from what each payee has actually cost — the median of its
                  last three payments. An estimate, not a bill: nothing here is owed
                  until a statement is entered.
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" className="fw-num">{fmt0(forecast.expected_total)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  expected{forecast.not_opened > 0 ? ` · ${forecast.not_opened} not opened` : ''}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={0.5}>
              {forecast.forecast.map((f) => (
                <Stack key={f.payee_id} direction="row" alignItems="center" spacing={1.5}
                  sx={{ py: 0.75, borderBottom: '1px solid var(--border-soft)' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{f.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {f.due_date ? `due ${f.due_date.slice(0, 10)}` : 'no typical due day'}
                      {f.times_paid > 0 && f.last_amount
                        ? ` · last ${fmt(f.last_amount)}${f.last_paid_on ? ` on ${f.last_paid_on.slice(0, 10)}` : ''} · ${f.times_paid}×`
                        : ' · never paid'}
                      {f.last_from_account_name ? ` · from ${f.last_from_account_name}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 96 }}>
                    <Typography variant="body2" className="fw-num">
                      {f.amount_due ? fmt(f.amount_due) : f.expected_amount ? `~${fmt(f.expected_amount)}` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {f.amount_due ? 'billed' : f.expected_amount ? 'estimated' : 'no history'}
                    </Typography>
                  </Box>
                  {f.bill_id ? (
                    f.bill_status === 'Paid' ? (
                      <Chip size="small" label="paid" variant="outlined"
                        sx={{ height: 20, color: 'var(--inflow-text)',
                              borderColor: 'var(--inflow-text)' }} />
                    ) : (
                      <Tooltip title={f.amount_due
                        ? 'Record this statement as paid, reusing last month\'s account'
                        : 'No statement yet — repeats the previous payment amount'}>
                        <span>
                          <Button size="small" variant="outlined" disabled={busy}
                            onClick={() => quickPay(f.bill_id!, f.name)}>
                            Quick pay
                          </Button>
                        </span>
                      </Tooltip>
                    )
                  ) : (
                    <Tooltip title="No bill for this month yet">
                      <span>
                        <Button size="small" disabled={busy} onClick={rollForward}>
                          Open
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {f.bill_id && f.bill_status !== 'Paid' && (
                    <Tooltip title="Quick pay and record a processing fee">
                      <span>
                        <Button size="small" disabled={busy}
                          onClick={() => setFeePay({
                            billId: f.bill_id!, payeeName: f.name,
                            amount: f.amount_due ?? f.expected_amount,
                          })}>
                          + fee
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit payee: paid-from and liability accounts, due day">
                    <IconButton size="small" aria-label={`Edit payee ${f.name}`}
                      onClick={() => openEditPayee(f.payee_id)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {suggestions.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Detected recurring payments
            </Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}>
              These left an account on a regular schedule for a similar amount,
              but are not tracked as bills yet. Nothing is created until you add one.
            </Typography>
            <Stack spacing={0.5}>
              {suggestions.map((sg) => (
                <Stack key={sg.name} direction="row" alignItems="center" spacing={1.5}
                  sx={{ py: 0.75, borderBottom: '1px solid var(--border-soft)' }}>
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                    {sg.name}
                  </Typography>
                  <Chip size="small" label={sg.category} sx={{ height: 20 }} />
                  <Typography variant="caption" color="text.secondary">
                    ~{fmt(sg.median_amount)} · day {sg.typical_day} · {sg.occurrences}×
                  </Typography>
                  <Button size="small" onClick={() => acceptSuggestion(sg)}>Add</Button>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {notice && (
        <Alert
          severity={
            notice.startsWith('Recorded') || notice.startsWith('Confirmed')
              || notice.startsWith('Matched') || notice.startsWith('Saved')
              || notice.startsWith('Opened') || notice.startsWith('Added')
              ? 'success'
              : notice.startsWith('No new matches') || notice.includes('already open')
                ? 'info' : 'error'
          }
          onClose={() => setNotice(null)}
          sx={{ mb: 2 }}
        >
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Status</Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
              <CountTile n={s.count_overdue} label="Overdue" varName="--danger-text" />
              <CountTile n={s.count_due} label="Due" varName="--warn-text" />
              <CountTile n={s.count_paid} label="Paid" varName="--inflow-text" />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 2 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Paid by category</Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {data.categories.map((c, i) => (
                <Box key={c.category}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">{c.category}</Typography>
                    <Typography variant="body2" className="fw-num" color="text.secondary">
                      {fmt0(c.total_paid)} · {c.count}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(Number(c.total_paid) / maxCat) * 100}
                    sx={{ '& .MuiLinearProgress-bar': { background: palette[i % palette.length] } }}
                  />
                </Box>
              ))}
              {data.categories.length === 0 && (
                <Typography variant="body2" color="text.secondary">No bills in range</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent sx={{ pb: 0 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab value="action" label={`Needs action (${bills.filter((b) => needsAction(b)).length})`} />
            <Tab value="paid" label={`Paid (${s.count_paid})`} />
            <Tab value="all" label="All" />
          </Tabs>

          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Payee</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{b.payee_name}</Typography>
                      {b.payee_attributed_to && (
                        <Typography variant="caption" color="text.secondary">
                          {b.payee_attributed_to}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" label={b.payee_category} /></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {b.payee_owner ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell className="fw-num">{b.period.slice(0, 7)}</TableCell>
                    <TableCell className="fw-num">{b.due_date?.slice(0, 10) ?? '—'}</TableCell>
                    <TableCell align="right" className="fw-num">
                      {/* The amount opens the transactions behind it. */}
                      <Button size="small" variant="text" className="fw-num"
                        aria-label={`${b.payee_name} ${b.amount_due ? fmt(b.amount_due) : 'no amount'}, view transactions`}
                        onClick={() => setDrill({ id: b.id, payeeName: b.payee_name })}
                        sx={{ minWidth: 0, px: 0.75, fontWeight: 600, color: 'inherit', textTransform: 'none',
                          textDecoration: 'underline dotted', textUnderlineOffset: 3,
                          '&.Mui-focusVisible': { outline: '2px solid var(--accent)' } }}>
                        {b.amount_due ? fmt(b.amount_due) : '—'}
                      </Button>
                    </TableCell>
                    <TableCell align="right" className="fw-num"
                      sx={{ color: Number(b.amount_outstanding) > 0 ? 'var(--warn-text)' : 'var(--text-faint)' }}>
                      {Number(b.amount_outstanding) > 0 ? fmt(b.amount_outstanding) : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{
                          width: 7, height: 7, borderRadius: '50%',
                          background: `var(${statusVar(b.effective_status)})`,
                        }} />
                        <Typography variant="caption" sx={{ color: `var(${statusVar(b.effective_status)})` }}>
                          {b.effective_status}
                        </Typography>
                        {b.payment_method === 'AutoPay' && (
                          <Tooltip title="Paid automatically">
                            <Chip size="small" label="auto" sx={{ height: 18 }} />
                          </Tooltip>
                        )}
                        {b.carries_balance && (b.coverage === 'minimum' || b.coverage === 'partial') && (
                          <Tooltip title={`${fmt(b.revolving)} still revolving and accruing interest`}>
                            <Chip size="small" label={b.coverage === 'minimum' ? 'min only' : 'partial'}
                              sx={{ height: 18, color: 'var(--danger-text)',
                                    borderColor: 'var(--danger-text)' }} variant="outlined" />
                          </Tooltip>
                        )}
                        <ScheduleChip bill={b} />
                        {b.reconciliation === 'unposted' && (
                          <Tooltip title="Marked paid, but no matching transaction ever appeared">
                            <Chip size="small" label="not posted"
                              sx={{ height: 18, color: 'var(--warn-text)',
                                    borderColor: 'var(--warn-text)' }} variant="outlined" />
                          </Tooltip>
                        )}
                        {b.reconciliation === 'matched' && (
                          <Tooltip title={`Matched to a bank transaction${b.posted_on ? ` posted ${b.posted_on.slice(0, 10)}` : ''}`}>
                            <Chip size="small" label="posted"
                              sx={{ height: 18, color: 'var(--inflow-text)',
                                    borderColor: 'var(--inflow-text)' }} variant="outlined" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {b.effective_status !== 'Paid' && (
                          <Tooltip title="Pay in full, reusing the account and rail from last month">
                            <span>
                              <Button size="small" variant="contained" disabled={busy}
                                onClick={() => quickPay(b.id, b.payee_name)}>
                                Quick pay
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {b.effective_status !== 'Paid' && (
                          <Tooltip title="Quick pay and record a processing fee">
                            <span>
                              <Button size="small" disabled={busy}
                                onClick={() => setFeePay({
                                  billId: b.id, payeeName: b.payee_name,
                                  amount: b.amount_outstanding !== '0' ? b.amount_outstanding : b.amount_due,
                                })}>
                                + fee
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {Number(b.amount_due ?? 0) > 0 ? (
                          <Button size="small" onClick={() => openPay(b)}>
                            {b.effective_status === 'Paid' ? 'Edit payment' : 'Details'}
                          </Button>
                        ) : (
                          // An opened month with no amount yet: the statement
                          // has arrived, so filling it in is the next step.
                          <Button size="small" onClick={() => openBill(b)}>
                            Enter amount
                          </Button>
                        )}
                        {b.effective_status === 'Paid' && b.reconciliation !== 'matched' && (
                          <Tooltip title="Record what the bank actually took">
                            <Button size="small" variant="outlined" onClick={() => openPosted(b)}>
                              Confirm posted
                            </Button>
                          </Tooltip>
                        )}
                        {b.reconciliation === 'matched' && (
                          <CheckCircleOutline fontSize="small" sx={{ color: 'var(--inflow-text)' }} />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                        <ReceiptLong sx={{ color: 'var(--text-faint)' }} />
                        <Typography variant="body2" color="text.secondary">
                          {tab === 'action' ? 'Nothing needs action right now' : 'No bills here'}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={!!paying} onClose={() => !busy && setPaying(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This records a payment you already made. FolioWorth does not move money.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {paying?.payee_name} · {paying?.period.slice(0, 7)}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Statement balance" size="small" fullWidth value={payStatement}
              onChange={(e) => setPayStatement(e.target.value)}
              helperText="What the biller says is owed this period"
            />
            {paying?.carries_balance && (
              <TextField
                label="Minimum due" size="small" fullWidth value={payMinimum}
                onChange={(e) => setPayMinimum(e.target.value)}
                helperText="Used to tell a minimum-only payment from a partial one"
              />
            )}
            <TextField
              label="Amount paid" size="small" fullWidth value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />

            {/* Say plainly what this payment leaves behind, before it is saved.
                On a revolving account the remainder accrues interest. */}
            {(() => {
              const stmt = Number(payStatement || 0);
              const paid = Number(payAmount || 0);
              if (!stmt || !paid) return null;
              const left = Math.max(stmt - paid, 0);
              if (left <= 0.01) {
                return (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    Pays the statement in full.
                  </Alert>
                );
              }
              const min = Number(payMinimum || 0);
              const isMin = min > 0 && paid <= min + 0.01;
              return (
                <Alert severity={paying?.carries_balance ? 'error' : 'warning'} sx={{ py: 0.5 }}>
                  {isMin ? 'Minimum only — ' : 'Partial payment — '}
                  <strong>{fmt(left)}</strong>{' '}
                  {paying?.carries_balance
                    ? 'will keep accruing interest.'
                    : 'will remain outstanding.'}
                </Alert>
              );
            })()}

            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined"
                onClick={() => setPayAmount(payStatement)}
                disabled={!payStatement}>
                Pay in full
              </Button>
              {paying?.carries_balance && payMinimum && (
                <Button size="small" variant="outlined"
                  onClick={() => setPayAmount(payMinimum)}>
                  Minimum only
                </Button>
              )}
            </Stack>
            <TextField
              label="Paid on" type="date" size="small" fullWidth value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <AccountSelect
              label="Paid from" accounts={accounts} types={FUNDING_TYPES}
              value={payFrom} onChange={setPayFrom} allowNone
              helperText="The account the money left — a card, if paid via Chexy. Blank uses the payee's default."
            />
            <TextField
              label="Payment fee" size="small" fullWidth value={payFee}
              onChange={(e) => setPayFee(e.target.value)}
              helperText="What it cost to pay: card processing, Chexy. Not added to the bill."
            />
            <TextField
              label="Paid via" select size="small" fullWidth value={payMethod}
              onChange={(e) => setPayMethod(e.target.value as BillPaymentMethod)}
              helperText="How it was settled this month. Only this bill changes."
            >
              {PAYMENT_METHODS.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaying(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitPayment} disabled={busy || !payAmount}>
            {busy ? 'Recording…' : 'Record payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={billOpen} onClose={() => !busy && setBillOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{billEditing ? 'Edit bill' : 'Add a bill'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One month for one payee. Record the payment separately once it is
            made — entering an amount here never marks anything paid.
          </Typography>
          <Stack spacing={2}>
            {/* Searchable for the same reason as the account pickers: a
                household has dozens of payees, and a plain select is a
                scroll hunt. Inactive payees are left out of a NEW bill. */}
            <Autocomplete
              size="small" fullWidth disableClearable
              options={payees
                .filter((p) => p.is_active || p.id === billPayee)
                .slice().sort((a, b) => a.name.localeCompare(b.name))}
              value={payees.find((p) => p.id === billPayee) ?? (null as unknown as BillPayee)}
              onChange={(_, p) => p && onBillPayeeChange(p.id)}
              getOptionLabel={(p) => p?.name ?? ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={!!billEditing}
              renderInput={(params) => <TextField {...params} label="Payee" />}
            />
            <TextField
              label="Month" type="month" size="small" fullWidth
              value={billPeriod.slice(0, 7)}
              onChange={(e) => setBillPeriod(`${e.target.value}-01`)}
              InputLabelProps={{ shrink: true }}
              disabled={!!billEditing}
            />
            <TextField
              label="Due date" type="date" size="small" fullWidth value={billDue}
              onChange={(e) => setBillDue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="A statement can fall due in a different month than its period"
            />
            <TextField
              label="Amount" size="small" fullWidth value={billAmount}
              onChange={(e) => setBillAmount(e.target.value)}
              helperText="The statement balance. Leave blank for a month with nothing owed."
            />
            <TextField
              label="Minimum due" size="small" fullWidth value={billMinimum}
              onChange={(e) => setBillMinimum(e.target.value)}
              helperText="Optional. Lets a minimum-only payment be told from a partial one."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitBill} disabled={busy || !billPayee}>
            {busy ? 'Saving…' : 'Save bill'}
          </Button>
        </DialogActions>
      </Dialog>

      <PayeeDialog
        open={addOpen} editing={payeeEditing} draft={payeeDraft}
        accounts={accounts} busy={busy}
        onClose={() => setAddOpen(false)} onSave={submitPayee}
      />

      <BillTransactionsDialog target={drill} onClose={() => setDrill(null)} />

      <QuickPayFeeDialog
        target={feePay} busy={busy}
        onClose={() => setFeePay(null)}
        onSubmit={(id, name, input) => quickPay(id, name, input)}
      />

      {/* Confirming what the bank took is a separate act from saying you paid.
          Keeping them apart is what surfaces a payment that never landed. */}
      <Dialog open={!!posting} onClose={() => !busy && setPosting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm the bank posted it</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This records what actually left the account, which can differ from
            what was entered — payments post late, short, or twice.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {posting?.payee_name} · {posting?.period.slice(0, 7)}
            {posting?.amount_paid ? ` · entered ${fmt(posting.amount_paid)}` : ''}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Posted on" type="date" size="small" fullWidth value={postDate}
              onChange={(e) => setPostDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Amount the bank took" size="small" fullWidth value={postAmount}
              onChange={(e) => setPostAmount(e.target.value)}
            />
            {(() => {
              const entered = Number(posting?.amount_paid ?? 0);
              const took = Number(postAmount || 0);
              if (!entered || !took || Math.abs(entered - took) <= 0.01) return null;
              return (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Differs from the {fmt(entered)} entered by{' '}
                  <strong>{fmt(Math.abs(entered - took))}</strong>.
                </Alert>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPosting(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitPosted} disabled={busy || !postAmount}>
            {busy ? 'Saving…' : 'Confirm posted'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Metric: React.FC<{ label: string; value: string; varName: string }> = ({ label, value, varName }) => (
  <Box>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography className="fw-num" variant="h5" sx={{ color: `var(${varName})` }}>{value}</Typography>
  </Box>
);

const CountTile: React.FC<{ n: number; label: string; varName: string }> = ({ n, label, varName }) => (
  <Box>
    <Typography className="fw-num" variant="h4" sx={{ color: `var(${varName})` }}>{n}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);
